import React, { useState } from 'react';
import './SolutionsPage.css';

const fallbackImage = '/dts-assets/ai/solutions-hero.png';

const handleImageError = (fallback) => (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = fallback;
};

const HeroBlock = ({ label, title, text, image, chipOne = 'One Platform', chipTwo = 'Full Visibility' }) => (
  <section className="enhanced-hero" style={{ backgroundImage: `url(${image})` }}>
    <div className="container enhanced-hero-grid">
      <div className="enhanced-copy reveal-left">
        <span className="section-label eyebrow-dark">{label}</span>
        <h1 dangerouslySetInnerHTML={{ __html: title }} />
        <p>{text}</p>

        <div className="hero-actions">
          <a href="/contact-us" className="btn btn-primary">Get Free Consultation</a>
          <a href="/packages" className="btn btn-outline">View Packages</a>
        </div>

        <div className="hero-metrics">
          <span><strong>GPS</strong>Tracking</span>
          <span><strong>Fleet</strong>Control</span>
          <span><strong>24/7</strong>Support</span>
        </div>
      </div>

      <div className="hero-visual-wrap reveal">
        <div className="floating-orbit" />
        <div className="float-chip one"><b />{chipOne}</div>
        <div className="float-chip two"><b />{chipTwo}</div>
        <div className="hero-visual">
          <img src={image} alt={label} onError={handleImageError(fallbackImage)} />
        </div>
      </div>
    </div>
  </section>
);

const solutions = [
  {
    title: 'Corporate Solutions',
    image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1000&q=80',
    link: '/corporate-solution',
    text: 'Complete fleet tracking, fuel monitoring, supply chain control, video monitoring, temperature monitoring, and driver safety modules.',
    details: [
      'Fleet Tracking',
      'Supply Chain Management',
      'Vehicle Video Monitoring',
      'Fuel Monitoring',
      'Temperature Monitoring',
      'Driver Profiling & Safety Violations'
    ]
  },
  {
    title: 'Consumer Solutions',
    image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=1000&q=80',
    link: '/consumer-solution',
    text: 'Premium mobile app tracking, reports, trip reports, and violation reports for personal vehicle users.',
    details: [
      'Premium Mobile App',
      'Reports',
      'Trip Reports',
      'Violations Report',
      'Live Vehicle Status',
      'Security Alerts'
    ]
  },
  {
    title: 'Asset Tracking',
    image: 'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?auto=format&fit=crop&w=1000&q=80',
    link: '/contact-us',
    text: 'Track valuable assets, equipment, generators, machinery, containers, and movable resources with smart GPS monitoring.',
    details: [
      'Asset Monitoring',
      'Genset Monitoring',
      'Container Tracking',
      'Equipment Tracking',
      'Movement Alerts',
      'Location History'
    ]
  },
  {
    title: 'Fuel Monitoring',
    image: 'https://images.unsplash.com/photo-1542367597-8849eb950fd8?auto=format&fit=crop&w=1000&q=80',
    link: '/corporate-solution',
    text: 'Monitor fuel level, fuel usage, refilling, fuel drops, leakage, and possible theft to reduce operational losses.',
    details: [
      'Fuel Level Tracking',
      'Fuel Theft Alerts',
      'Fuel Drop Detection',
      'Fuel Usage Reports',
      'Refill Monitoring',
      'Fleet Efficiency'
    ]
  },
  {
    title: '4G Dash Cam Monitoring',
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1000&q=80',
    link: '/corporate-solution',
    text: 'Monitor vehicle movement with dash cameras, live video support, road visibility, and incident review.',
    details: [
      'Live Video Monitoring',
      'Driving Event Recording',
      'Incident Review',
      'Driver Safety',
      'Road Facing Camera',
      'Fleet Visibility'
    ]
  },
  {
    title: 'Temperature Monitoring',
    image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=1000&q=80',
    link: '/corporate-solution',
    text: 'Protect cold chain and sensitive goods with temperature alerts, monitoring dashboard, and history reports.',
    details: [
      'Live Temperature Data',
      'Cold Chain Control',
      'Threshold Alerts',
      'Temperature Reports',
      'Cargo Safety',
      'Sensitive Goods Monitoring'
    ]
  }
];

const DetailModal = ({ item, onClose }) => {
  if (!item) return null;

  return (
    <div className="solution-modal-backdrop" onClick={onClose}>
      <div className="solution-modal" onClick={(e) => e.stopPropagation()}>
        <button className="solution-modal-close" onClick={onClose} type="button">×</button>

        <img src={item.image} alt={item.title} onError={handleImageError(fallbackImage)} />

        <div className="solution-modal-content">
          <span className="section-label">DTS Solution</span>
          <h2>{item.title}</h2>
          <p>{item.text}</p>

          <ul>
            {item.details.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>

          <a href={item.link} className="btn btn-primary">Explore Solution</a>
        </div>
      </div>
    </div>
  );
};

const SolutionsPage = () => {
  const [selectedSolution, setSelectedSolution] = useState(null);

  return (
    <main className="enhanced-page solutions-enhanced">
      <HeroBlock
        label="Solutions"
        title="Smart Tracking Solutions for <em>Every Need</em>"
        text="DTS provides complete GPS tracking and telematics solutions for personal vehicles, commercial fleets, rental operations, assets, fuel, video, and specialized monitoring requirements."
        image="/dts-assets/ai/solutions-hero.png"
      />

      <section className="enhanced-section alt">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">Solution Categories</span>
            <h2>Choose the Right Tracking <em>System</em></h2>
            <div className="divider" />
            <p>
              DTS solutions are structured for different operational goals —
              from vehicle security to full fleet optimization.
            </p>
          </div>

          <div className="solutions-showcase-grid">
            {solutions.map((item) => (
              <button
                className="solutions-showcase-card reveal"
                key={item.title}
                type="button"
                onClick={() => setSelectedSolution(item)}
              >
                <img src={item.image} alt={item.title} onError={handleImageError(fallbackImage)} />

                <div className="solutions-showcase-content">
                  <span>DTS Solution</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <b>View Details</b>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="enhanced-section">
        <div className="container split-grid">
          <div className="feature-panel reveal-left">
            <span className="section-label">Why DTS Solutions</span>
            <h2>One Platform for Vehicle, Fleet, Asset & Fuel Control</h2>
            <p>
              Our solutions help customers monitor vehicles, reduce losses, improve driver
              safety, control fuel usage, manage trips, and access important reports from
              mobile and web dashboards.
            </p>

            <div className="solution-benefit-list">
              <span>✓ Real-time GPS tracking</span>
              <span>✓ Web and mobile access</span>
              <span>✓ Theft and tamper alerts</span>
              <span>✓ Fuel and fleet reports</span>
              <span>✓ Driver behavior monitoring</span>
              <span>✓ 24/7 support and recovery assistance</span>
            </div>
          </div>

          <div className="media-card reveal">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80"
              alt="Tracking dashboard"
              onError={handleImageError(fallbackImage)}
            />
          </div>
        </div>
      </section>

      <section className="enhanced-section dark-band">
        <div className="container process-grid">
          {[
            ['01', 'Track', 'View real-time GPS location and route history.'],
            ['02', 'Alert', 'Receive alerts for ignition, geofence, tamper, overspeed, and unusual activity.'],
            ['03', 'Analyze', 'Use reports to understand trips, mileage, fuel usage, and driver behavior.'],
            ['04', 'Control', 'Manage vehicles and assets through app, web portal, and support channels.']
          ].map((item) => (
            <div className="process-card reveal" key={item[1]}>
              <span>{item[0]}</span>
              <h3>{item[1]}</h3>
              <p>{item[2]}</p>
            </div>
          ))}
        </div>
      </section>

      <DetailModal item={selectedSolution} onClose={() => setSelectedSolution(null)} />
    </main>
  );
};

export default SolutionsPage;